class FathomTracker {
  private queue: Array<{ event: string; value?: number }> = [];
  private isReady = false;
  private checkInterval: NodeJS.Timeout | null = null;
  private subscribers: Set<() => void> = new Set();
  private timeoutId: NodeJS.Timeout | null = null;

  constructor() {
    // Only run in browser
    if (typeof window !== 'undefined') {
      this.checkFathomAvailability();
    }
  }

  private checkFathomAvailability() {
    if (window.fathom) {
      this.markAsReady();
      return;
    }

    this.checkInterval = setInterval(() => {
      if (window.fathom) {
        this.markAsReady();
      }
    }, 100);

    // Timeout after 10 seconds
    this.timeoutId = setTimeout(() => {
      if (!this.isReady) {
        console.warn('Fathom analytics failed to load within 10 seconds');
        this.cleanup();
      }
    }, 10000);
  }

  private markAsReady() {
    this.isReady = true;
    this.flushQueue();
    this.notifySubscribers();
    this.cleanup();
  }

  private cleanup() {
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
      this.checkInterval = null;
    }
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
  }

  private flushQueue() {
    while (this.queue.length > 0) {
      const event = this.queue.shift();
      if (event && window.fathom) {
        try {
          window.fathom.trackEvent(event.event, event.value);
        } catch (error) {
          console.error('Error tracking queued event:', error);
        }
      }
    }
  }

  private notifySubscribers() {
    this.subscribers.forEach((callback) => {
      try {
        callback();
      } catch (error) {
        console.error('Error in Fathom subscriber callback:', error);
      }
    });
  }

  subscribe(callback: () => void): () => void {
    this.subscribers.add(callback);

    return () => {
      this.subscribers.delete(callback);
    };
  }

  trackEvent(event: string, value?: number): void {
    if (this.isReady && typeof window !== 'undefined' && window.fathom) {
      try {
        window.fathom.trackEvent(event, value);
      } catch (error) {
        console.error('Error tracking Fathom event:', error);
      }
    } else {
      // Queue the event for later
      this.queue.push({ event, value });
    }
  }

  get ready(): boolean {
    return this.isReady;
  }

  get queueLength(): number {
    return this.queue.length;
  }

  forceReady(): void {
    if (!this.isReady) {
      this.markAsReady();
    }
  }

  destroy(): void {
    this.cleanup();
    this.subscribers.clear();
    this.queue.length = 0;
  }
}

const fathomTracker = new FathomTracker();

if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    fathomTracker.destroy();
  });
}

export default fathomTracker;
